import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { catchError, tap } from 'rxjs/operators';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { DirectoryEditRestrictionsState } from 'src/app/store/dashboard/directory-edit-restrictions/directory-edit-restrictions.state';
import { DirectoryStructureSelectors } from 'src/app/store/samwin/directory/directory-structure/directory-structure.selectors';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { LongRunningContextStarted, LongRunningContextStopped } from 'src/app/store/server-communication/running-actions/running-actions.actions';
import { RemoveWindow, RestoreWindow, SetWindowIndex } from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { SamwebWindowState } from 'src/app/store/utility/samweb-window/samweb-window.state';
import {
   BooleanOrgField,
   DateTimeOrgField,
   DirectoriesService,
   DirectoryEntryRemoteDto,
   Int32OrgField,
   OrganisationEntryRemoteDto,
   StringOrgField,
} from 'src/generated/swagger';
import { DirectorySettingsState } from '../../../../brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import {
   ClearAndStartLoadingAllDirectoryValues,
   EditEntryInAllDirectories,
   RefreshEditedDirectoryEntry,
   RemoveEntryFromAllDirectories,
   RemoveUnusedSelectedEntries,
} from '../directory-browser-behaviour/directory-browser-behaviour.actions';
import { DirectoryBrowserBehaviourState } from '../directory-browser-behaviour/directory-browser-behaviour.state';
import { DirectoryEntryHelper } from '../directory-entry-helper';
import {
   DirectoryBrowserAddEditedEntry,
   DirectoryBrowserAddEntry,
   DirectoryBrowserCreateEditEntry,
   DirectoryBrowserDeleteEditedEntry,
   DirectoryBrowserEditEntry,
   DirectoryBrowserEditedClearUpdatedEntry,
   DirectoryBrowserEditedSetConfirmationInProgress,
   DirectoryBrowserEditedUpdateOriginEntry,
   DirectoryBrowserRemoveEditEntry,
   DirectoryBrowserUpdateEditEntry,
   DirectoryBrowserUpdateEditedEntry,
   DirectoryBrowserUpdateOriginEntry,
} from './directory-editing-behaviour.actions';

export interface DirectoryEntryEditInfo {
   originalEntry: DirectoryEntryRemoteDto;
   editedEntry: DirectoryEntryRemoteDto;
   updatedEntry: DirectoryEntryRemoteDto;
   sourceBrickInstanceId: string;
   isConfirmationInProgress: boolean;
}

export interface DirectoryEditingBehaviourStateModel {
   directoryEntriesInEditing: DirectoryEntryEditInfo[];
}

export interface DirectoryEntryEditInfo {
   originalEntry: DirectoryEntryRemoteDto;
   editedEntry: DirectoryEntryRemoteDto;
   updatedEntry: DirectoryEntryRemoteDto;
   sourceBrickInstanceId: string;
   isConfirmationInProgress: boolean;
}

@State<DirectoryEditingBehaviourStateModel>({
   name: 'directoryEditingBehaviour',
   defaults: { directoryEntriesInEditing: [] },
   children: [DirectoryEditRestrictionsState],
})
@Injectable()
export class DirectoryEditingBehaviourState {
   constructor(private directoriesService: DirectoriesService, private store: Store) {}

   @Selector([DirectoryEditingBehaviourState])
   public static directoryEntriesInEditing(state: DirectoryEditingBehaviourStateModel) {
      return state?.directoryEntriesInEditing ?? [];
   }

   @Selector([DirectoryEditingBehaviourState.directoryEntriesInEditing])
   public static getEntriesInEditing(directoryEntriesInEditing: DirectoryEntryEditInfo[]) {
      return directoryEntriesInEditing.map((entry) => entry.editedEntry);
   }

   @Selector([DirectoryBrowserBehaviourState.getSelectedDirectoryID, DirectoryEditingBehaviourState.getEntriesInEditing])
   public static isCurrentEntryEdited(getSelectedId: (brickInstanceId: string) => number, editedEntries: DirectoryEntryRemoteDto[]) {
      return (brickInstanceId: string) => {
         return editedEntries.some((entry) => entry.id === getSelectedId(brickInstanceId));
      };
   }

   @Selector([DirectoryEditingBehaviourState.directoryEntriesInEditing])
   public static getEntriesInEditingIds(directoryEntriesInEditing: DirectoryEntryEditInfo[]) {
      return directoryEntriesInEditing.map((entry) => entry.editedEntry.id);
   }

   @Selector([DirectoryEditingBehaviourState.getEntriesInEditing])
   public static getEditedDirectoryValuesById(entries: DirectoryEntryRemoteDto[]) {
      return (entryId: number) => {
         return entries.find((entry) => entry.id === entryId);
      };
   }

   @Selector([DirectoryEditingBehaviourState.directoryEntriesInEditing])
   public static getEditedDirectoryInfoById(directoryEntriesInEditing: DirectoryEntryEditInfo[]) {
      return (entryId: number) => {
         return directoryEntriesInEditing.find((entry) => entry.originalEntry.id === entryId);
      };
   }

   @Selector([
      DirectoryEditingBehaviourState.getEditedDirectoryInfoById,
      DirectoryStructureState.editableColumns,
      DirectoryStructureSelectors.getColumnInstance,
   ])
   public static areEditableEntriesChanged(
      getInfo: (entryId: number) => DirectoryEntryEditInfo,
      columns: DirectoryColumn[],
      getColumnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ) {
      return (entryId: number) => {
         const entry = getInfo(entryId);
         const isEqual = columns
            .filter((column) => column.organisationUnitNumber === '00')
            .map((column) => {
               const origin = getColumnInstance(entry?.originalEntry, column)?.value ?? '';
               const mine = getColumnInstance(entry?.editedEntry, column)?.value ?? '';
               return origin === mine;
            });
         return _(isEqual).includes(false);
      };
   }

   @Selector([
      DirectoryEditingBehaviourState.getEditedDirectoryInfoById,
      DirectoryStructureState.editableColumns,
      DirectoryStructureSelectors.getColumnInstance,
   ])
   public static getEntriesForComparison(
      getInfo: (entryId: number) => DirectoryEntryEditInfo,
      columns: DirectoryColumn[],
      getColumnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ) {
      return (entryId: number) => {
         const entry = getInfo(entryId);
         if (_.isNil(entry?.updatedEntry)) {
            return [];
         }
         const getEntriesForComparison = columns
            .filter((column) => column.organisationUnitNumber === '00')
            .filter((column) => {
               const origin = getColumnInstance(entry?.originalEntry, column)?.value ?? '';
               const mine = getColumnInstance(entry?.editedEntry, column)?.value ?? '';
               const theirs = getColumnInstance(entry?.updatedEntry, column)?.value ?? '';
               return !(origin === theirs || mine === theirs);
            })
            .map((column) => column);
         return getEntriesForComparison;
      };
   }

   @Selector([
      DirectoryEditingBehaviourState.getEditedDirectoryInfoById,
      DirectoryStructureState.editableColumns,
      DirectoryStructureSelectors.getColumnInstance,
   ])
   public static areEditableEntriesEqual(
      getInfo: (entryId: number) => DirectoryEntryEditInfo,
      columns: DirectoryColumn[],
      getColumnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ) {
      return (entryId: number) => {
         const entry = getInfo(entryId);
         if (_.isNil(entry?.updatedEntry)) {
            return true;
         }

         const isEqual = columns
            .filter((column) => column.organisationUnitNumber === '00')
            .map((column) => {
               const origin = getColumnInstance(entry?.originalEntry, column)?.value ?? '';
               const mine = getColumnInstance(entry?.editedEntry, column)?.value ?? '';
               const theirs = getColumnInstance(entry?.updatedEntry, column)?.value ?? '';
               return origin === theirs || mine === theirs;
            });
         return !_(isEqual).includes(false);
      };
   }

   @Selector([DirectoryEditingBehaviourState.getEditedDirectoryInfoById])
   public static getEditedDirectoryBrickById(getInfo: (entryId: number) => DirectoryEntryEditInfo) {
      return (entryId: number) => {
         return getInfo(entryId).sourceBrickInstanceId;
      };
   }

   @Selector([DirectoryEditingBehaviourState.getEditedDirectoryInfoById])
   public static isConfirmationInProgress(getInfo: (entryId: number) => DirectoryEntryEditInfo) {
      return (entryId: number) => {
         return getInfo(entryId)?.isConfirmationInProgress ?? false;
      };
   }

   @Selector([DirectoryEditingBehaviourState.getEntriesInEditing])
   public static getNewIdforEditEntries(entries: DirectoryEntryRemoteDto[]) {
      let id: number = _(entries)
         .filter((entry) => entry?.id < 0)
         .map((entry) => entry?.id)
         .min();
      if (_.isNull(id) || _.isUndefined(id)) {
         id = 0;
      }
      id -= 1;
      return id;
   }

   @Action(DirectoryBrowserAddEntry)
   @ImmutableContext()
   public directoryBrowserAddEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserAddEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         const id: number = this.store.selectSnapshot(DirectoryEditingBehaviourState.getNewIdforEditEntries);
         const entry: DirectoryEntryRemoteDto = DirectoryEntryHelper.createNewDirectoryEntry(id);
         const selectedDirId: number = this.store.selectSnapshot(DirectorySettingsState.getDefaultSelectedDirectory)(action.brickInstanceId).id;
         const dirKey: string = 'org' + this.store.selectSnapshot(DirectoryStructureState.selectedDirectoryOrganisationEntry).organizationalUnitNumber;
         entry[dirKey].value = selectedDirId;
         entry[dirKey].isValueSet = true;
         state.directoryEntriesInEditing.push({
            originalEntry: entry,
            editedEntry: entry,
            updatedEntry: null,
            sourceBrickInstanceId: action.brickInstanceId,
            isConfirmationInProgress: false,
         });
         return state;
      });
   }

   @Action(DirectoryBrowserEditEntry)
   @ImmutableContext()
   public directoryBrowserEditEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserEditEntry) {
      if (!this.store.selectSnapshot(DirectoryEditingBehaviourState.isCurrentEntryEdited)(action.brickInstanceId)) {
         ctx.dispatch(new DirectoryBrowserCreateEditEntry(action.brickInstanceId));
      } else {
         const index = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
         const windowId = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSelectedDirectoryID)(action.brickInstanceId).toString();
         ctx.dispatch(new SetWindowIndex('directory', windowId, index + 1));
         ctx.dispatch(new RestoreWindow('directory', windowId));
      }
   }

   @Action(DirectoryBrowserCreateEditEntry)
   @ImmutableContext()
   public directoryBrowserCreateEditEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserCreateEditEntry) {
      const id = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSelectedDirectoryID)(action.brickInstanceId);

      return this.directoriesService.queryDirectoryEntry({ entryId: id }).pipe(
         tap((result) => {
            ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
               if (!_.isNil(result)) {
                  state.directoryEntriesInEditing.push({
                     originalEntry: result.resultEntry,
                     editedEntry: result.resultEntry,
                     updatedEntry: null,
                     sourceBrickInstanceId: action.brickInstanceId,
                     isConfirmationInProgress: false,
                  });
               }
               return state;
            });
         })
      );
   }

   @Action(DirectoryBrowserRemoveEditEntry)
   @ImmutableContext()
   public directoryBrowserRemoveEditEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserRemoveEditEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         _.remove(state.directoryEntriesInEditing, (x) => x.originalEntry.id === action.id);
         return state;
      });
   }

   @Action(DirectoryBrowserUpdateOriginEntry)
   @ImmutableContext()
   public directoryBrowserUpdateOriginEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserUpdateOriginEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         // Try to filter out type, when object and not null it should be an orgfield.
         const entries = state.directoryEntriesInEditing.map((entry) => entry?.originalEntry);
         if (
            typeof entries.find((entry) => entry?.id === action.id)[action.field] === 'object' &&
            !_.isNil(entries.find((entry) => entry?.id === action.id)[action.field])
         ) {
            entries.find((entry) => entry?.id === action.id)[action.field].value = action.value;
            entries.find((entry) => entry?.id === action.id)[action.field].isValueSet = true;
         } else {
            entries.find((entry) => entry?.id === action.id)[action.field] = action.value;
         }
         return state;
      });
   }

   @Action(DirectoryBrowserUpdateEditEntry)
   @ImmutableContext()
   public directoryBrowserUpdateEditEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserUpdateEditEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         // Try to filter out type, when object and not null it should be an orgfield.
         const entries = state.directoryEntriesInEditing.map((entry) => entry?.editedEntry);
         if (
            typeof entries.find((entry) => entry?.id === action.id)[action.field] === 'object' &&
            !_.isNil(entries.find((entry) => entry?.id === action.id)[action.field])
         ) {
            entries.find((entry) => entry?.id === action.id)[action.field].value = action.value;
            entries.find((entry) => entry?.id === action.id)[action.field].isValueSet = true;
         } else {
            entries.find((entry) => entry?.id === action.id)[action.field] = action.value;
         }
         return state;
      });
   }

   @Action(DirectoryBrowserEditedClearUpdatedEntry)
   @ImmutableContext()
   public directoryBrowserEditedClearUpdatedEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserEditedClearUpdatedEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         const entries = state.directoryEntriesInEditing.map((entry) => entry?.updatedEntry);
         let updatedEntry = entries.find((entry) => entry?.id === action.id);
         updatedEntry = null;
         return state;
      });
   }

   @Action(DirectoryBrowserEditedUpdateOriginEntry)
   @ImmutableContext()
   public directoryBrowserEditedUpdateOriginEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserEditedUpdateOriginEntry) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         const info = state.directoryEntriesInEditing.find((entry) => entry.originalEntry.id === action.id);
         info.originalEntry = info.updatedEntry;
         info.editedEntry.orgupdate = info.originalEntry.orgupdate;
         info.updatedEntry = null;
         return state;
      });
   }

   @Action(DirectoryBrowserEditedSetConfirmationInProgress)
   @ImmutableContext()
   public directoryBrowserEditedSetConfirmationInProgress(
      ctx: StateContext<DirectoryEditingBehaviourStateModel>,
      action: DirectoryBrowserEditedSetConfirmationInProgress
   ) {
      ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
         const info = state.directoryEntriesInEditing.find((entry) => entry.originalEntry.id === action.id);
         info.isConfirmationInProgress = action.isConfirmationInProgress;
         return state;
      });
   }

   @Action(DirectoryBrowserAddEditedEntry)
   @ImmutableContext()
   public directoryBrowserAddEditedEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserAddEditedEntry) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
      const editedEntry: DirectoryEntryRemoteDto = this.store.selectSnapshot(DirectoryEditingBehaviourState.getEditedDirectoryValuesById)(action.id);
      const result = this.directoriesService.addDirectoryEntry({
         entry: editedEntry,
      });

      return result.pipe(
         tap(() => {
            ctx.dispatch(new DirectoryBrowserRemoveEditEntry(action.id));
            ctx.dispatch(new RemoveWindow('directory', action.id.toString()));
            ctx.dispatch(new RemoveUnusedSelectedEntries());
            ctx.dispatch(new ClearAndStartLoadingAllDirectoryValues());
            ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
         })
      );
   }

   @Action(DirectoryBrowserUpdateEditedEntry)
   @ImmutableContext()
   public directoryBrowserEditedEntriesUpdate(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserUpdateEditedEntry) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
      const editedEntry: DirectoryEntryRemoteDto = this.store.selectSnapshot(DirectoryEditingBehaviourState.getEditedDirectoryValuesById)(action.id);
      const result = this.directoriesService.editDirectoryEntry({
         entry: editedEntry,
      });
      return result.pipe(
         tap(() => {
            ctx.dispatch(new EditEntryInAllDirectories(action.id, editedEntry));
            ctx.dispatch(new DirectoryBrowserRemoveEditEntry(action.id));
            ctx.dispatch(new RemoveWindow('directory', action.id.toString()));
            ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
         }),
         catchError((error: HttpErrorResponse) => {
            // TODO: If available in the future (angular 12) use HttpStatusCode.Conflict
            if (error.status === 409) {
               ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
               return ctx.dispatch(new RefreshEditedDirectoryEntry(action.id));
            }
            console.error('Error during update directory', error);
            ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
            return;
         })
      );
   }

   @Action(DirectoryBrowserDeleteEditedEntry)
   @ImmutableContext()
   public directoryBrowserDeleteEditedEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: DirectoryBrowserDeleteEditedEntry) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
      const result = this.directoriesService.deleteDirectoryEntry({
         entryId: action.id,
      });
      return result.pipe(
         tap(() => {
            ctx.dispatch(new RemoveEntryFromAllDirectories(action.id));
            ctx.dispatch(new DirectoryBrowserRemoveEditEntry(action.id));
            ctx.dispatch(new RemoveWindow('directory', action.id.toString()));
            ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
         })
      );
   }

   @Action(RefreshEditedDirectoryEntry)
   @ImmutableContext()
   public refreshEditedDirectoryEntry(ctx: StateContext<DirectoryEditingBehaviourStateModel>, action: RefreshEditedDirectoryEntry) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
      return this.directoriesService.queryDirectoryEntry({ entryId: action.id }).pipe(
         tap((result) => {
            ctx.setState((state: DirectoryEditingBehaviourStateModel) => {
               const info = state.directoryEntriesInEditing.find((entry) => entry.originalEntry.id === action.id);
               info.updatedEntry = result.resultEntry;
               return state;
            });
            ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.DIRECTORY_WINDOW, action.id.toString()));
         }),
         tap(() => {
            if (this.store.selectSnapshot(DirectoryEditingBehaviourState.areEditableEntriesEqual)(action.id)) {
               ctx.dispatch(new DirectoryBrowserEditedUpdateOriginEntry(action.id));
               ctx.dispatch(new DirectoryBrowserUpdateEditedEntry(action.id));
            }
         })
      );
   }
}
